/**
 * Data Export Card
 * Configure data export preferences and retention settings
 *
 * Follows Polaris design guidelines:
 * - Uses Polaris spacing scale (gap="400")
 * - Clear section headers
 * - Help text explains each option
 */

import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Select,
  Box,
  Tooltip,
  Icon,
  Divider,
  Collapsible,
  InlineGrid,
  RangeSlider,
} from '@shopify/polaris';
import { InfoIcon, ExportIcon, ChevronUpIcon, ChevronDownIcon } from '@shopify/polaris-icons';
import { useCallback } from 'react';
import React from 'react';
import { Toggle } from '../../ui';

type ExportFormat = 'csv' | 'excel' | 'json';
type ExportFrequency = 'daily' | 'weekly' | 'monthly';
type RetentionDays = 30 | 60 | 90 | 180 | 365;

interface DataExportCardProps {
  exportFormat: ExportFormat;
  autoExportEnabled: boolean;
  autoExportFrequency: ExportFrequency;
  dataRetentionDays: RetentionDays;
  onExportFormatChange: (format: ExportFormat) => void;
  onAutoExportEnabledChange: (enabled: boolean) => void;
  onAutoExportFrequencyChange: (frequency: ExportFrequency) => void;
  onDataRetentionChange: (days: RetentionDays) => void;
  /** Whether this section is collapsible */
  isCollapsible?: boolean;
  /** Whether the section is open (only used when isCollapsible is true) */
  isOpen?: boolean;
  /** Handler for toggling the section (only used when isCollapsible is true) */
  onToggle?: () => void;
}

const RETENTION_VALUES: RetentionDays[] = [30, 60, 90, 180, 365];

const frequencyOptions = [
  { label: 'Daily - Export every day at midnight', value: 'daily' },
  { label: 'Weekly - Export every Monday at midnight', value: 'weekly' },
  { label: 'Monthly - Export on the 1st of each month', value: 'monthly' },
];

/**
 * CSV Icon - Document with comma-separated rows
 * Uses purple brand colors with soft gradient background
 */
function CsvIcon() {
  const id = React.useId();
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`${id}-bg`} x1="4" y1="4" x2="28" y2="28" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ede9f9" />
          <stop offset="100%" stopColor="#d4ccf0" />
        </linearGradient>
        <filter id={`${id}-shadow`} x="-2" y="-2" width="36" height="36">
          <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#8b7bd8" floodOpacity="0.15" />
        </filter>
      </defs>
      {/* Background card */}
      <rect x="4" y="4" width="24" height="24" rx="4" fill={`url(#${id}-bg)`} filter={`url(#${id}-shadow)`} />
      {/* Document with folded corner */}
      <path
        d="M8 8h10l4 4v12a1 1 0 01-1 1H9a1 1 0 01-1-1V9a1 1 0 011-1z"
        fill="white"
        stroke="#8b7bd8"
        strokeWidth="1"
      />
      {/* Folded corner */}
      <path d="M18 8v4h4" fill="none" stroke="#8b7bd8" strokeWidth="1" strokeLinejoin="round" />
      {/* CSV rows with comma separators */}
      <g stroke="#6b5bb8" strokeWidth="1.5" strokeLinecap="round">
        <line x1="10" y1="14" x2="13" y2="14" />
        <circle cx="14.5" cy="14" r="0.5" fill="#6b5bb8" />
        <line x1="16" y1="14" x2="20" y2="14" />
        <line x1="10" y1="17" x2="12" y2="17" />
        <circle cx="13.5" cy="17" r="0.5" fill="#6b5bb8" />
        <line x1="15" y1="17" x2="20" y2="17" />
        <line x1="10" y1="20" x2="14" y2="20" />
        <circle cx="15.5" cy="20" r="0.5" fill="#6b5bb8" />
        <line x1="17" y1="20" x2="20" y2="20" />
      </g>
    </svg>
  );
}

/**
 * Excel Icon - Spreadsheet grid with cells
 * Uses purple brand colors with soft gradient background
 */
function ExcelIcon() {
  const id = React.useId();
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`${id}-bg`} x1="4" y1="4" x2="28" y2="28" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ede9f9" />
          <stop offset="100%" stopColor="#d4ccf0" />
        </linearGradient>
        <filter id={`${id}-shadow`} x="-2" y="-2" width="36" height="36">
          <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#8b7bd8" floodOpacity="0.15" />
        </filter>
      </defs>
      {/* Background card */}
      <rect x="4" y="4" width="24" height="24" rx="4" fill={`url(#${id}-bg)`} filter={`url(#${id}-shadow)`} />
      {/* Spreadsheet container */}
      <rect x="7" y="7" width="18" height="18" rx="2" fill="white" stroke="#8b7bd8" strokeWidth="1" />
      {/* Header row */}
      <rect x="7" y="7" width="18" height="4" rx="2" fill="#8b7bd8" />
      <rect x="7" y="9" width="18" height="2" fill="#8b7bd8" />
      {/* Grid lines */}
      <g stroke="#d4ccf0" strokeWidth="0.75">
        {/* Horizontal lines */}
        <line x1="7" y1="15" x2="25" y2="15" />
        <line x1="7" y1="19" x2="25" y2="19" />
        {/* Vertical lines */}
        <line x1="13" y1="11" x2="13" y2="25" />
        <line x1="19" y1="11" x2="19" y2="25" />
      </g>
      {/* Cell highlights */}
      <rect x="13.5" y="15.5" width="5" height="3" rx="0.5" fill="#f5f3fb" />
      <rect x="19.5" y="19.5" width="5" height="3" rx="0.5" fill="#ede9f9" />
    </svg>
  );
}

/**
 * JSON Icon - Code brackets with data structure
 * Uses purple brand colors with soft gradient background
 */
function JsonIcon() {
  const id = React.useId();
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`${id}-bg`} x1="4" y1="4" x2="28" y2="28" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ede9f9" />
          <stop offset="100%" stopColor="#d4ccf0" />
        </linearGradient>
        <filter id={`${id}-shadow`} x="-2" y="-2" width="36" height="36">
          <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#8b7bd8" floodOpacity="0.15" />
        </filter>
      </defs>
      {/* Background card */}
      <rect x="4" y="4" width="24" height="24" rx="4" fill={`url(#${id}-bg)`} filter={`url(#${id}-shadow)`} />
      {/* Code window */}
      <rect x="7" y="7" width="18" height="18" rx="2" fill="white" stroke="#8b7bd8" strokeWidth="1" />
      {/* Window header with dots */}
      <rect x="7" y="7" width="18" height="4" rx="2" fill="#8b7bd8" />
      <rect x="7" y="9" width="18" height="2" fill="#8b7bd8" />
      <circle cx="10" cy="9" r="1" fill="#d4ccf0" />
      <circle cx="13" cy="9" r="1" fill="#d4ccf0" />
      <circle cx="16" cy="9" r="1" fill="#d4ccf0" />
      {/* JSON brackets */}
      <g stroke="#6b5bb8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
        {/* Opening brace */}
        <path d="M11 13c-1.5 0-1.5 1-1.5 1v2.5s0 1-1 1c1 0 1 1 1 1v2.5s0 1 1.5 1" />
        {/* Closing brace */}
        <path d="M21 13c1.5 0 1.5 1 1.5 1v2.5s0 1 1 1c-1 0-1 1-1 1v2.5s0 1-1.5 1" />
      </g>
      {/* Key-value indicators */}
      <g fill="#8b7bd8">
        <rect x="13" y="14" width="3" height="1.5" rx="0.5" />
        <rect x="17" y="14" width="2" height="1.5" rx="0.5" opacity="0.6" />
        <rect x="13" y="17" width="2" height="1.5" rx="0.5" />
        <rect x="16" y="17" width="3" height="1.5" rx="0.5" opacity="0.6" />
        <rect x="13" y="20" width="4" height="1.5" rx="0.5" />
        <rect x="18" y="20" width="2" height="1.5" rx="0.5" opacity="0.6" />
      </g>
    </svg>
  );
}

function FormatSelectionCard({
  title,
  description,
  icon,
  selected,
  onClick,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        cursor: 'pointer',
        padding: '12px',
        borderRadius: '8px',
        border: `1px solid ${selected ? '#7C3AED' : 'var(--p-color-border)'}`,
        backgroundColor: selected ? '#F3F0FF' : 'var(--p-color-bg-surface)',
        transition: 'all 0.2s ease',
        height: '100%',
      }}
    >
      <BlockStack gap="200" align="start">
        <InlineStack align="space-between" blockAlign="center">
          {icon}
          {selected && (
            <div
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#7C3AED',
              }}
            />
          )}
        </InlineStack>
        <BlockStack gap="050">
          <Text as="p" variant="bodyMd" fontWeight="semibold" tone={selected ? 'base' : 'subdued'}>
            {title}
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            {description}
          </Text>
        </BlockStack>
      </BlockStack>
    </div>
  );
}

export function DataExportCard({
  exportFormat,
  autoExportEnabled,
  autoExportFrequency,
  dataRetentionDays,
  onExportFormatChange,
  onAutoExportEnabledChange,
  onAutoExportFrequencyChange,
  onDataRetentionChange,
  isCollapsible = false,
  isOpen = true,
  onToggle,
}: DataExportCardProps) {
  const handleFormatChange = useCallback(
    (value: ExportFormat) => {
      onExportFormatChange(value);
    },
    [onExportFormatChange]
  );

  const handleFrequencyChange = useCallback(
    (value: string) => {
      onAutoExportFrequencyChange(value as ExportFrequency);
    },
    [onAutoExportFrequencyChange]
  );

  const handleRetentionChange = useCallback(
    (value: string) => {
      onDataRetentionChange(parseInt(value, 10) as RetentionDays);
    },
    [onDataRetentionChange]
  );

  // Map the current retention days to an index (0-4) for the slider
  const retentionIndex = RETENTION_VALUES.indexOf(dataRetentionDays);
  // Default to 0 if not found
  const currentSliderValue = retentionIndex !== -1 ? retentionIndex : 0;

  const handleRetentionSliderChange = useCallback(
    (value: number) => {
      const selectedDays = RETENTION_VALUES[value];
      if (selectedDays) {
        onDataRetentionChange(selectedDays);
      }
    },
    [onDataRetentionChange]
  );

  const content = (
    <Box paddingBlockStart="200">
      <BlockStack gap="500">
        {/* Export Format */}
        <BlockStack gap="300">
          <Text as="p" variant="bodyMd" fontWeight="semibold">
            Default export format
          </Text>
          <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
            <FormatSelectionCard
              title="CSV"
              description="Best for spreadsheets"
              icon={<CsvIcon />}
              selected={exportFormat === 'csv'}
              onClick={() => handleFormatChange('csv')}
            />
            <FormatSelectionCard
              title="Excel"
              description="Native formatting"
              icon={<ExcelIcon />}
              selected={exportFormat === 'excel'}
              onClick={() => handleFormatChange('excel')}
            />
            <FormatSelectionCard
              title="JSON"
              description="For developers"
              icon={<JsonIcon />}
              selected={exportFormat === 'json'}
              onClick={() => handleFormatChange('json')}
            />
          </InlineGrid>
        </BlockStack>

        <Divider />

        {/* Auto Export */}
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="start" wrap={false}>
            <BlockStack gap="100">
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                Automatic exports
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Automatically export your inventory data on a schedule
              </Text>
            </BlockStack>
            <Box paddingBlockStart="100">
              <Toggle
                label="Enable automatic exports"
                enabled={autoExportEnabled}
                onChange={onAutoExportEnabledChange}
              />
            </Box>
          </InlineStack>

          {autoExportEnabled && (
            <Box
              paddingInlineStart="400"
              paddingBlockStart="200"
              borderInlineStartWidth="025"
              borderColor="border"
            >
              <Select
                label="Export frequency"
                options={frequencyOptions}
                value={autoExportFrequency}
                onChange={handleFrequencyChange}
                helpText="Exports are sent to your primary email address"
              />
            </Box>
          )}
        </BlockStack>

        <Divider />

        {/* Data Retention */}
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Data retention
            </Text>
            <div
              style={{
                padding: '4px 12px',
                borderRadius: '8px',
                backgroundColor: 'var(--p-color-bg-surface-secondary)',
                color: 'var(--p-color-text)',
                border: '1px solid var(--p-color-border)',
                fontWeight: 600,
                fontSize: '13px',
              }}
            >
              {dataRetentionDays} days
            </div>
          </InlineStack>

          <RangeSlider
            label="Data retention period"
            labelHidden
            value={currentSliderValue}
            min={0}
            max={RETENTION_VALUES.length - 1}
            step={1}
            onChange={handleRetentionSliderChange}
            output
          />

          <Box
            background="bg-surface-secondary"
            padding="300"
            borderRadius="200"
          >
            <Text as="p" variant="bodySm" tone="subdued">
              Older data will be automatically archived. Longer retention enables better seasonal analysis.
            </Text>
          </Box>
        </BlockStack>
      </BlockStack>
    </Box>
  );

  const header = isCollapsible ? (
    <InlineStack align="space-between" blockAlign="start">
      <InlineStack gap="200" blockAlign="start">
        <Box paddingBlockStart="050">
          <Icon source={ExportIcon} tone="subdued" />
        </Box>
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">
            Data and export
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            Set your preferred export format, automatic export schedule, and data retention period.
          </Text>
        </BlockStack>
      </InlineStack>
      <Box paddingBlockStart="050">
        <Icon source={isOpen ? ChevronUpIcon : ChevronDownIcon} tone="subdued" />
      </Box>
    </InlineStack>
  ) : (
    <InlineStack align="space-between" blockAlign="start">
      <InlineStack gap="200" blockAlign="start">
        <Box paddingBlockStart="050">
          <Icon source={ExportIcon} tone="subdued" />
        </Box>
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">
            Data and export
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            Set your preferred export format, automatic export schedule, and data retention period.
          </Text>
        </BlockStack>
      </InlineStack>
      <Tooltip content="Configure how your data is exported and retained">
        <Box paddingBlockStart="050">
          <Icon source={InfoIcon} tone="subdued" />
        </Box>
      </Tooltip>
    </InlineStack>
  );

  return (
    <Card>
      <BlockStack gap="400">
        {/* Header */}
        {isCollapsible ? (
          <button
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-controls="section-data-export"
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {header}
          </button>
        ) : (
          header
        )}

        {/* Content */}
        {isCollapsible ? (
          <Collapsible
            open={isOpen}
            id="section-data-export"
            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
          >
            {content}
          </Collapsible>
        ) : (
          content
        )}
      </BlockStack>
    </Card>
  );
}
